import { apiClient } from './client'
import type { Exercise } from './exercises'

/**
 * Банк калиброванных заданий адаптивного теста уровня (админ).
 *
 * Задания банка спрятаны на сервере глобальным скоупом и по обычным маршрутам
 * `/exercises` недоступны никому, включая админа: увиденное заранее задание
 * обесценивает тест, а вместе с ним и уровень в профиле ученика. Поэтому у
 * банка свой контур — этот.
 */

/** Навык, который меряет позиция банка. Совпадает с навыками языкового профиля. */
export type PlacementSkill = 'grammar_vocabulary' | 'reading' | 'listening' | 'writing' | 'speaking'

export type PlacementLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'

/** Навыки второй ступени: закрытые задания с автопроверкой. */
export const PLACEMENT_CLOSED_SKILLS: PlacementSkill[] = ['grammar_vocabulary', 'reading', 'listening']

export const PLACEMENT_SKILLS: PlacementSkill[] = [...PLACEMENT_CLOSED_SKILLS, 'writing', 'speaking']

/** Уровни, которые различает тест. C2 в банк не входит: закрытыми заданиями он не меряется. */
export const PLACEMENT_LEVELS: PlacementLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1']

export type PlacementBankItem = {
  id: number
  skill: PlacementSkill
  level: PlacementLevel
  /** Порядок внутри связки «уровень + навык»; совпадает с порядком в файле сидера */
  position: number
  /** Выключенная позиция остаётся в банке, но в подбор блока не идёт */
  is_active: boolean
  /** Проба третьей ступени: письмо или говорение — оценивается не автоматически */
  is_probe: boolean
  /** Критерии оценки пробы для ИИ-черновика; у закрытых заданий null */
  evaluation_criteria: Record<string, string> | null
  /** Задание позиции — с эталонами: без них вычитка невозможна */
  exercise: Exercise
  updated_at: string | null
}

export type PlacementBankSummaryRow = {
  skill: PlacementSkill
  level: PlacementLevel
  total: number
  active: number
}

export type PlacementBankUpdate = {
  title?: string
  instructions?: string | null
  content?: Record<string, unknown>
  status?: 'draft' | 'ready'
  level?: PlacementLevel
  position?: number
  is_active?: boolean
  evaluation_criteria?: Record<string, string> | null
}

type Paginated<T> = {
  data: T[]
  meta?: { total?: number; current_page?: number; per_page?: number; last_page?: number }
}

export const placementBankApi = {
  async list(
    params: {
      skill?: PlacementSkill
      level?: PlacementLevel
      is_active?: boolean
      q?: string
      page?: number
      per_page?: number
    } = {},
  ): Promise<Paginated<PlacementBankItem>> {
    const { is_active: isActive, ...rest } = params

    const { data } = await apiClient.get<Paginated<PlacementBankItem>>('/admin/placement-bank', {
      params: {
        ...rest,
        // Явные '0'/'1', а не boolean: axios сериализует false в строку
        // «false», а правило `boolean` в Laravel такую строку не принимает —
        // фильтр отдавал бы 422 вместо списка.
        ...(isActive === undefined ? {} : { is_active: isActive ? '1' : '0' }),
      },
    })

    return data
  },

  async summary(): Promise<PlacementBankSummaryRow[]> {
    const { data } = await apiClient.get<{ data: PlacementBankSummaryRow[] }>('/admin/placement-bank/summary')
    return data.data
  },

  async update(id: number, payload: PlacementBankUpdate): Promise<PlacementBankItem> {
    const { data } = await apiClient.patch<{ data: PlacementBankItem }>(`/admin/placement-bank/${id}`, payload)
    return data.data
  },

  /**
   * Озвучить аудирование системным синтезом.
   *
   * Отдельная кнопка, а не побочный эффект сохранения: синтез платный для
   * платформы, и каждая правка запятой в скрипте стоила бы денег.
   */
  async voice(id: number): Promise<PlacementBankItem> {
    const { data } = await apiClient.post<{ data: PlacementBankItem }>(`/admin/placement-bank/${id}/voice`)
    return data.data
  },
}
